import { Stack } from '@mui/material';

import useFlexBoxStyles from 'common/flexBox';
import LayoutForm from 'common/layouts/LayoutForm';
import { useFormFields } from 'libs/hooksLib';
import { isEqual } from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getUser } from 'redux/slices/authSlice';
import { cancelCloseCustomDialogAction, closeCustomDialogAction } from 'redux/slices/customDialogSlice';
import { getNestedDialogStateData } from 'redux/slices/nestedDialogSlice';

import FormNav from 'common/components/FormNav';
import { ErrorCode } from 'common/constants/errorCodes';
import ServiceFormBookingOptions from './ServiceFormBookingOptions';

import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import { getDynamicFields, getNestedData } from 'common/features/renderForms/utils';
import { useSettingValue } from 'common/hooks/useSettingValue';
import { testFeatureEnabled } from 'redux/slices/authSlice';
import { getFormConfig } from 'redux/slices/formSlice';
import {
    fetchServiceIssueStatsAction,
    fetchServicePreviewIssuesAction,
    markServiceChangedAction,
} from 'redux/slices/iCalendarV4';
import ServiceFormCalendars from './ServiceFormCalendars';
import ServiceFormSettings from './ServiceFormSettings';
import ServicePreviewIssuesDialog from './ServicePreviewIssuesDialog';

const ServiceForm = (props) => {
    const { action, service, onSubmit } = props;
    const { classes: flexBox, cx } = useFlexBoxStyles();

    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const formConfig = useSelector(getFormConfig);
    const dispatch = useDispatch();
    const nestedDialogStateData = useSelector(getNestedDialogStateData);
    const user = useSelector(getUser);
    const [subForm, setSubForm] = useState(null);
    const [selectedCalendars, setSelectedCalendars] = useState([]);
    const [previewIssues, setPreviewIssues] = useState(null);
    const pendingSaveRef = useRef(null);
    const confirmTitle = t('common.cancelConfirm');
    const confirmMessage = t('services.cancelMessage');
    const isTestFeatureEnabled = useSelector(testFeatureEnabled);
    // Saving a service with issues is allowed by default; the setting can disable it.
    const allowSaveWithIssuesSetting = useSettingValue(SettingsKey.ENABLE_SAVE_SERVICE_WITH_ISSUES);
    const allowSaveWithIssues = allowSaveWithIssuesSetting !== false;

    const [step, setStep] = useState(1);
    const visibleSteps = [
        t('event.eventFormNav.settings'),
        t('services.form.bookingOptions'),
        t('appointment.form.asset'),
    ];

    const handleSetSteep = (s) => {
        setStep(s);
        // if (isValidForm()) {
        //     closeSubForm();
        //     setStep(s);
        // }
    };

    useEffect(() => {
        if (service?.calendars?.length > 0) {
            const calendars = service.calendars.map((sc) => ({
                ...sc,
                ...sc.calendar,
            }));
            setSelectedCalendars(calendars);
        }
    }, [service?.calendars]);

    const initialForm = useMemo(() => {
        const initial = {
            name: service?.name || '',
            dateRanges: service?.dateRanges || [],
            mainImage: service?.mainImage || '',
            active: service?.active ?? true,
            userGroupId: service?.userGroupId || user?.userGroupId || null,
            serviceType: service?.serviceType || '',
            sharingParams: service?.sharingParams || null,

            bookingDurationMinutes: service?.bookingOptions?.bookingDurationMinutes ?? 1,
            bookingBufferTimeMinutes: service?.bookingOptions?.bookingBufferTimeMinutes ?? 0,
            minAdvanceBookingMinutes: (service?.bookingOptions?.minAdvanceBookingMinutes ?? 0) / 60 / 24,
            maxAdvanceBookingMinutes: (service?.bookingOptions?.maxAdvanceBookingMinutes ?? 0) / 60 / 24,
            slotIntervalMinutes: service?.bookingOptions?.slotIntervalMinutes ?? 5,
            maxGuestsPerBooking: service?.bookingOptions?.maxGuestsPerBooking ?? 0,
            allowGuestMultipleBookings: service?.bookingOptions?.allowGuestMultipleBookings ?? false,
            allowAgentOverlappingBookings: service?.bookingOptions?.allowAgentOverlappingBookings ?? true,
            enableVideoCall: service?.bookingOptions?.enableVideoCall ?? false,
            bookingCutoffDate: service?.bookingOptions?.bookingCutoffDate ?? null,
            deniedMultipleAppointment: service?.bookingOptions?.deniedMultipleAppointment ?? false,
            calendarType: service?.calendarType || 'asset',
            taxonomies: service?.taxonomies || [user.userGroupFacet || null]?.filter(Boolean),
            slotStartOffsetMinutes: service?.bookingOptions?.slotStartOffsetMinutes ?? 0,

            // duration: service?.duration ?? 0,
            // timeBetween: service?.timeBetween ?? 0,
            // minTimeToSchedule: service?.minTimeToSchedule ?? 0,
            // maxTimeToSchedule: service?.maxTimeToSchedule ?? 0,
            // incrementTime: service?.incrementTime ?? 0,
            // slotCapacity: service?.slotCapacity ?? 1,
            // allowOverlapping: service?.allowOverlapping ?? true,
            // hasICall: service?.hasICall ?? false,
            // bookingUntil: service?.bookingUntil ?? undefined,
        };
        const dynamic = formConfig ? getDynamicFields(formConfig, service?.extraFields) : null;
        return { ...initial, ...dynamic };
    }, [user, service, formConfig]);

    const [fields, handleFieldChange, resetForm] = useFormFields(nestedDialogStateData || initialForm);

    const [openCancelDlg, setOpenCancelDlg] = useState(false);

    const userDidChange = useCallback(() => {
        const { ...original } = initialForm;
        const { ...current } = fields;
        return !isEqual(original, current);
    }, [fields, initialForm]);

    const restart = useCallback(() => {
        resetForm(initialForm);
    }, [initialForm, resetForm]);

    const finalCancel = useCallback(() => {
        restart();
        dispatch(closeCustomDialogAction());
    }, [dispatch, restart]);

    const cancelDlg = (event) => {
        setOpenCancelDlg(false);
        dispatch(cancelCloseCustomDialogAction());
    };

    const manageCancel = useCallback(() => {
        if (!userDidChange()) {
            finalCancel();
        } else {
            setOpenCancelDlg(true);
        }
    }, [finalCancel, userDidChange]);

    const cancel = (event) => {
        event.preventDefault();
        manageCancel();
    };

    const proceedSave = useCallback(
        (data) => {
            onSubmit(
                data,
                () => {
                    dispatch(closeCustomDialogAction());
                    resetForm(initialForm);
                    dispatch(closeCustomDialogAction());
                    dispatch(fetchServiceIssueStatsAction());
                    // Record the change so the affected-services view can warn that
                    // issue stats take a few minutes to refresh.
                    dispatch(markServiceChangedAction());
                },
                (error) => {
                    const errorData = error?.response?.data ?? error?.data ?? error;
                    const fallbackMessage = errorData?.description || t('app.common.error');
                    const message =
                        errorData?.code === ErrorCode.DuplicateEntity
                            ? t('services.form.duplicateEntity', { defaultValue: fallbackMessage })
                            : t('app.common.error');
                    enqueueSnackbar(message, { variant: 'error' });
                },
            );
        },
        [dispatch, enqueueSnackbar, initialForm, onSubmit, resetForm, t],
    );

    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();
            const data = {
                name: fields.name,
                dateRanges: fields.dateRanges,
                mainImage: fields.mainImage,
                active: fields.active,
                userGroupId: fields.userGroupId,
                taxonomies: fields.taxonomies,
                serviceType: fields.serviceType,
                sharingParams: fields.sharingParams,
                bookingOptions: {
                    bookingDurationMinutes: fields.bookingDurationMinutes,
                    bookingBufferTimeMinutes: fields.bookingBufferTimeMinutes,
                    minAdvanceBookingMinutes: fields.minAdvanceBookingMinutes * 24 * 60,
                    maxAdvanceBookingMinutes: fields.maxAdvanceBookingMinutes * 24 * 60,
                    slotIntervalMinutes: fields.slotIntervalMinutes,
                    maxGuestsPerBooking: fields.maxGuestsPerBooking,
                    allowGuestMultipleBookings: fields.allowGuestMultipleBookings,
                    allowAgentOverlappingBookings: fields.allowAgentOverlappingBookings,
                    enableVideoCall: fields.enableVideoCall,
                    bookingCutoffDate: fields.bookingCutoffDate || null,
                    deniedMultipleAppointment: fields.deniedMultipleAppointment,
                    slotStartOffsetMinutes: fields.slotStartOffsetMinutes,
                },
                extraFields: getNestedData(formConfig, fields),
                calendars: selectedCalendars?.map((calendar) => {
                    if (calendar?.availability) {
                        return {
                            calendarId: calendar.canonicalId,
                            active: calendar.active,
                            ...(calendar.availability.id
                                ? {
                                      availabilityId: calendar.availability.id,
                                      availability: { ...calendar.availability },
                                  }
                                : { availability: calendar.availability }),
                        };
                    } else
                        return {
                            calendarId: calendar.canonicalId,
                            active: calendar.active,
                            availability: {},
                        };
                }),
            };

            // Preview the issues the pending changes would generate.
            // If any would be generated, ask the user to confirm before
            // saving; otherwise save straight away.
            if (isTestFeatureEnabled) {
                pendingSaveRef.current = data;
                dispatch(
                    fetchServicePreviewIssuesAction({
                        id: service?.id || undefined,
                        data,
                        onSuccess: (response) => {
                            const generated = (response?.issues || []).filter((issue) => issue.isValid === false);
                            if (generated.length > 0) {
                                setPreviewIssues(generated);
                            } else {
                                proceedSave(data);
                            }
                        },
                        // On preview failure, don't block the save.
                        onFailure: () => proceedSave(data),
                    }),
                );
                return;
            }

            proceedSave(data);
        },
        [dispatch, fields, formConfig, proceedSave, selectedCalendars, service, isTestFeatureEnabled],
    );

    const confirmSaveWithIssues = () => {
        setPreviewIssues(null);
        // Guard: never save with issues if the setting disallows it.
        if (allowSaveWithIssues && pendingSaveRef.current) {
            proceedSave(pendingSaveRef.current);
        }
        pendingSaveRef.current = null;
    };

    const cancelSaveWithIssues = () => {
        setPreviewIssues(null);
        pendingSaveRef.current = null;
    };

    const closeSubForm = () => {
        setSubForm(null);
    };

    const openSubForm = (form) => {
        setSubForm(form);
    };

    const isValidForm = () => {
        return fields.name.length > 0 && fields.mainImage && fields.serviceType;
    };

    return (
        <LayoutForm
            customClasses={flexBox.vH70}
            title={action}
            confirmDlg={{
                open: openCancelDlg,
                accept: finalCancel,
                cancel: cancelDlg,
                titleDlg: confirmTitle,
                messageDlg: confirmMessage,
            }}
        >
            <Stack direction="column" spacing={2} className={cx(flexBox.overflowHidden, flexBox.h100)}>
                {!subForm && (
                    <Stack direction="column" spacing={3} className={cx(flexBox.overflowHidden, flexBox.h100)}>
                        <FormNav step={step} setStep={handleSetSteep} steps={visibleSteps} wizardMode={true} />
                        <Stack direction="column" className={cx(flexBox.overflowHidden, flexBox.h100)}>
                            {
                                {
                                    1: (
                                        <ServiceFormSettings
                                            fields={fields}
                                            handleFieldChange={handleFieldChange}
                                            open={openSubForm}
                                            close={closeSubForm}
                                            setStep={setStep}
                                            step={step}
                                            cancel={cancel}
                                            service={service}
                                            isValidForm={isValidForm}
                                            setSelectedCalendars={setSelectedCalendars}
                                        />
                                    ),
                                    2: (
                                        <ServiceFormBookingOptions
                                            selectedCalendars={selectedCalendars}
                                            fields={fields}
                                            handleFieldChange={handleFieldChange}
                                            open={openSubForm}
                                            close={closeSubForm}
                                            setStep={setStep}
                                            step={step}
                                            cancel={cancel}
                                            service={service}
                                            isValidForm={isValidForm}
                                        />
                                    ),
                                    3: (
                                        <ServiceFormCalendars
                                            setSubForm={setSubForm}
                                            selectedCalendars={selectedCalendars}
                                            setSelectedCalendars={setSelectedCalendars}
                                            setStep={setStep}
                                            handleSubmit={handleSubmit}
                                            cancel={cancel}
                                            groupId={fields.userGroupId}
                                        />
                                    ),
                                }[step]
                            }
                        </Stack>
                    </Stack>
                )}
                {subForm}
            </Stack>
            {previewIssues && (
                <ServicePreviewIssuesDialog
                    open={true}
                    issues={previewIssues}
                    allowSave={allowSaveWithIssues}
                    onConfirm={confirmSaveWithIssues}
                    onCancel={cancelSaveWithIssues}
                />
            )}
        </LayoutForm>
    );
};

export default ServiceForm;
